// 1Y-online-tri: Three-state boolean pill control — "yes" / "no" / "both".
//
// Visually identical to the existing TriToggle / BooleanToggle (same
// `.dash-segmented` pill styling), but typed against the wire-format
// `TriBooleanValue` ("yes" | "no" | "both") so the filterStore's `online`
// field flows through verbatim. "both" is the inactive default — selecting
// it leaves the filter off; selecting "yes" or "no" filters the data.
//
// Created instead of widening BooleanToggle so the other 7 boolean toggles
// keep their (true | false | null) contract untouched.

export interface TriBooleanToggleProps {
  value: "yes" | "no" | "both";
  onChange: (v: "yes" | "no" | "both") => void;
  /** Tiny header label rendered above the control. */
  header?: string;
  /** Accessible label override for the group. */
  ariaLabel?: string;
  /** Optional label overrides; defaults to ["Yes", "No", "Both"]. */
  labels?: [string, string, string];
}

export function TriBooleanToggle(props: TriBooleanToggleProps): JSX.Element {
  const { value, onChange, header, ariaLabel, labels } = props;
  const [yes, no, both] = labels ?? ["Yes", "No", "Both"];

  return (
    <div className="dash-filter-field">
      {header ? <span className="dash-filter-field__header">{header}</span> : null}
      <div
        className="dash-segmented"
        role="group"
        aria-label={ariaLabel ?? header ?? "tri-boolean"}
      >
        <button
          type="button"
          className={`dash-segmented__pill${
            value === "yes" ? " dash-segmented__pill--active" : ""
          }`}
          onClick={() => onChange("yes")}
          aria-pressed={value === "yes"}
        >
          {yes}
        </button>
        <button
          type="button"
          className={`dash-segmented__pill${
            value === "no" ? " dash-segmented__pill--active" : ""
          }`}
          onClick={() => onChange("no")}
          aria-pressed={value === "no"}
        >
          {no}
        </button>
        <button
          type="button"
          className={`dash-segmented__pill${
            value === "both" ? " dash-segmented__pill--active" : ""
          }`}
          onClick={() => onChange("both")}
          aria-pressed={value === "both"}
        >
          {both}
        </button>
      </div>
    </div>
  );
}
